<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import BaseIcon from '@/components/ui/BaseIcon.vue';
import {
  ALPHABET_INDEX_IDLE_HIDE_MS,
  FAVORITES_INDEX_KEY,
  findScrollViewport,
  letterAtRatio,
} from '@/libs/helpers/alphabet-index';

// The bubble outlives the drag just long enough to confirm where the jump landed.
const BUBBLE_LINGER_MS = 400;
// Comfortable band height per letter; the strip only shrinks below it when the
// alphabet does not fit the viewport.
const LETTER_BAND = 18;
// Clearance kept above and below the strip, so it never covers the search bar.
const VERTICAL_INSET = 72;

const props = defineProps<{ letters: string[] }>();

const emit = defineEmits<{ select: [letter: string] }>();

const rootEl = ref<HTMLElement>();
const barEl = ref<HTMLElement>();
const viewportHeight = ref<number>(0);
const activeLetter = ref<string | null>(null);
const bubbleTop = ref<number>(0);
const draggingPointer = ref<number | null>(null);
const visible = ref<boolean>(false);

let lingerTimeout: ReturnType<typeof setTimeout>;
let hideTimeout: ReturnType<typeof setTimeout>;
let resizeObserver: ResizeObserver | undefined;
let scrollTarget: HTMLElement | Window | undefined;

const barStyle = computed(() => ({
  height: `min(${Math.max(viewportHeight.value - VERTICAL_INSET * 2, 0)}px, ${props.letters.length * LETTER_BAND}px)`,
  top: `${viewportHeight.value / 2}px`,
}));

onMounted(() => {
  const viewport = findScrollViewport(rootEl.value);
  viewportHeight.value = viewport?.clientHeight || window.innerHeight;

  scrollTarget = viewport ?? window;
  scrollTarget.addEventListener('scroll', keepVisible, { passive: true });

  if (!viewport || typeof ResizeObserver === 'undefined') return;
  // eslint-disable-next-line compat/compat -- iOS 11 is below our real support floor (plugin-legacy targets 'defaults, not IE 11'); .browserslistrc is intentionally broader.
  resizeObserver = new ResizeObserver(() => (viewportHeight.value = viewport.clientHeight));
  resizeObserver.observe(viewport);
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  scrollTarget?.removeEventListener('scroll', keepVisible);
  clearTimeout(lingerTimeout);
  clearTimeout(hideTimeout);
});

/**
 * Shows the strip and restarts its countdown. A drag holds it open for as long
 * as it lasts: the countdown only starts again once the finger is lifted.
 */
function keepVisible() {
  visible.value = true;
  clearTimeout(hideTimeout);
  if (draggingPointer.value === null)
    hideTimeout = setTimeout(() => (visible.value = false), ALPHABET_INDEX_IDLE_HIDE_MS);
}

function selectAt(clientY: number) {
  const bar = barEl.value;
  const root = rootEl.value;
  if (!bar || !root) return;

  const barRect = bar.getBoundingClientRect();
  const letter = letterAtRatio(props.letters, (clientY - barRect.top) / barRect.height);
  if (!letter) return;

  const index = props.letters.indexOf(letter);
  bubbleTop.value =
    barRect.top - root.getBoundingClientRect().top + ((index + 0.5) * barRect.height) / props.letters.length;

  if (letter === activeLetter.value) return;
  activeLetter.value = letter;
  emit('select', letter);
}

function onPointerDown(event: PointerEvent) {
  // The list must not scroll under the finger while it scrubs the strip.
  event.preventDefault();
  clearTimeout(lingerTimeout);
  draggingPointer.value = event.pointerId;
  keepVisible();
  // Keeps the drag alive once the finger wanders off the strip, sideways or past its ends.
  barEl.value?.setPointerCapture?.(event.pointerId);
  selectAt(event.clientY);
}

function onPointerMove(event: PointerEvent) {
  if (draggingPointer.value === event.pointerId) selectAt(event.clientY);
}

function onPointerUp(event: PointerEvent) {
  draggingPointer.value = null;
  keepVisible();
  lingerTimeout = setTimeout(() => (activeLetter.value = null), BUBBLE_LINGER_MS);
  if (barEl.value?.hasPointerCapture?.(event.pointerId)) barEl.value.releasePointerCapture(event.pointerId);
}
</script>

<template>
  <!-- Pointer-only shortcut over a list the reader can already walk item by
       item, hence hidden from assistive technologies. -->
  <div ref="rootEl" class="alphabet-index" aria-hidden="true">
    <div
      ref="barEl"
      class="alphabet-index__bar"
      :class="{ 'alphabet-index__bar--visible': visible }"
      :style="barStyle"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
    >
      <span
        v-for="letter in letters"
        :key="letter"
        class="alphabet-index__letter"
        :class="{ 'alphabet-index__letter--active': letter === activeLetter }"
      >
        <BaseIcon v-if="letter === FAVORITES_INDEX_KEY" name="bookmark" :size="12" />
        <template v-else>{{ letter }}</template>
      </span>
    </div>

    <Transition name="alphabet-index-bubble">
      <div v-if="activeLetter" class="alphabet-index__bubble" :style="{ top: `${bubbleTop}px` }">
        <BaseIcon v-if="activeLetter === FAVORITES_INDEX_KEY" name="bookmark" :size="22" />
        <template v-else>{{ activeLetter }}</template>
      </div>
    </Transition>
  </div>
</template>

<style lang="scss">
// Zero-height sticky anchor: it pins to the top of the scrolling viewport for
// the whole list, and positions the strip against the viewport rather than
// against the content scrolling past.
.alphabet-index {
  height: 0;
  position: sticky;
  top: 0;
  z-index: 2;

  // Only there while the list is being scrolled: hidden, it must not swallow
  // taps meant for the rows underneath.
  &__bar {
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    opacity: 0;
    padding: $spacing-2 6px;
    pointer-events: none;
    position: absolute;
    right: 2px;
    touch-action: none;
    transform: translateY(-50%);
    transition: opacity $duration-fade $easing-drawer;
    user-select: none;

    &--visible {
      opacity: 1;
      pointer-events: auto;
    }
  }

  &__letter {
    align-items: center;
    color: $color-neutral-higher;
    display: flex;
    flex: 1 1 0;
    font: 600 10px/1 $font-poppins;
    justify-content: center;
    min-height: 0;
    transition: color $duration-fade $easing-drawer;

    &--active {
      color: $color-primary;
    }
  }

  &__bubble {
    align-items: center;
    border-radius: $radius-pill;
    color: $color-neutral-highest;
    display: flex;
    font: $heading-h3;
    height: 48px;
    justify-content: center;
    letter-spacing: $heading-h3-spacing;
    pointer-events: none;
    position: absolute;
    right: $spacing-6;
    transform: translateY(-50%);
    width: 48px;

    // Pill and shadow carried by a layer of their own, so letting the list show
    // through them leaves the letter at full contrast.
    &::before {
      background-color: $color-white;
      border-radius: inherit;
      box-shadow: $shadow-md;
      content: '';
      inset: 0;
      opacity: 0.7;
      position: absolute;
      z-index: -1;
    }
  }
}

.alphabet-index-bubble-enter-active,
.alphabet-index-bubble-leave-active {
  transition: opacity $duration-drawer-close $easing-drawer;
}

.alphabet-index-bubble-enter-from,
.alphabet-index-bubble-leave-to {
  opacity: 0;
}
</style>
